import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Timeline,
  Tag,
  Badge,
  List,
  Progress,
  message,
  Spin,
} from 'antd';
import {
  ToolOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getTechnicianDashboardStats, TechnicianDashboardStats } from '../../services/statsApi';

const { Title, Text } = Typography;

const TechnicianDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<TechnicianDashboardStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      const data = await getTechnicianDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
      message.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Assigned Work Orders',
      value: stats?.assignedWorkOrders || 0,
      icon: <ToolOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      color: '#e6f7ff',
    },
    {
      title: 'In Progress',
      value: stats?.workOrdersInProgress || 0,
      icon: <PlayCircleOutlined style={{ fontSize: 24, color: '#faad14' }} />,
      color: '#fffbe6',
    },
    {
      title: 'Completed Today',
      value: stats?.completedToday || 0,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      color: '#f6ffed',
    },
    {
      title: 'Hours Logged',
      value: (stats?.hoursLoggedToday || 0).toFixed(1),
      suffix: 'hrs',
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#13c2c2' }} />,
      color: '#e6fffb',
    },
  ];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'blue',
      in_progress: 'orange',
      waiting_parts: 'purple',
      waiting_approval: 'cyan',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'default',
      normal: 'blue',
      high: 'orange',
      urgent: 'red',
    };
    return colors[priority] || 'default';
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div>
          <Title level={2}>Technician Dashboard</Title>
          <Text type="secondary">
            Welcome back, {user?.first_name}! Here are your assigned work orders.
          </Text>
        </div>

        {/* Stats Cards */}
        <Row gutter={[16, 16]}>
          {statCards.map((stat, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    suffix={stat.suffix}
                    valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                  />
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: stat.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {stat.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Quick Actions */}
        <Card title="Quick Actions">
          <Space wrap>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => navigate('/technician/clock-in')}
            >
              Clock In
            </Button>
            <Button
              icon={<ToolOutlined />}
              onClick={() => navigate('/technician/work-orders')}
            >
              View All Work Orders
            </Button>
            <Button
              icon={<WarningOutlined />}
              onClick={() => navigate('/technician/parts-request')}
            >
              Request Parts
            </Button>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => navigate('/technician/schedule')}
            >
              View Schedule
            </Button>
          </Space>
        </Card>

        {/* Main Content */}
        <Row gutter={[16, 16]}>
          {/* Assigned Work Orders */}
          <Col xs={24} lg={16}>
            <Card
              title={
                <Space>
                  <ToolOutlined />
                  <span>My Work Orders</span>
                  <Badge count={stats?.myWorkOrders.length || 0} />
                </Space>
              }
              extra={
                <Button
                  type="link"
                  onClick={() => navigate('/technician/work-orders')}
                >
                  View All
                </Button>
              }
            >
              <List
                dataSource={stats?.myWorkOrders || []}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => navigate(`/technician/work-orders/${item.id}`)}
                      >
                        Open
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{item.description}</Text>
                          <Tag color={getStatusColor(item.status)}>
                            {item.status.replace('_', ' ').toUpperCase()}
                          </Tag>
                          <Tag color={getPriorityColor(item.priority)}>
                            {item.priority.toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary">{item.customer_name}</Text>
                          <Text type="secondary">{item.vehicle}</Text>
                          <Text type="secondary">WO #{item.wo_number}</Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Today's Schedule & Time Tracking */}
          <Col xs={24} lg={8}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Today's Schedule */}
              <Card
                title={<><CalendarOutlined /> Today's Schedule</>}
              >
                {(stats?.todaysSchedule || []).length > 0 ? (
                  <Timeline
                    items={(stats?.todaysSchedule || []).map((item) => ({
                      children: (
                        <div>
                          <Text strong>{new Date(item.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</Text>
                          <br />
                          <Text>{item.service_type}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {item.customer_name} - {item.vehicle}
                          </Text>
                        </div>
                      ),
                    }))}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text type="secondary">No appointments scheduled for today</Text>
                  </div>
                )}
              </Card>

              {/* Time Tracking */}
              <Card title={<><ClockCircleOutlined /> Time Tracking</>}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text>Daily Hours</Text>
                      <Text strong>{(stats?.hoursLoggedToday || 0).toFixed(1)} / 8.0 hrs</Text>
                    </div>
                    <Progress
                      percent={Math.round(((stats?.hoursLoggedToday || 0) / 8.0) * 100)}
                      status="active"
                    />
                  </div>

                  <div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Clock In"
                          value={stats?.clockInTime ? new Date(stats.clockInTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '--:--'}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Clock Out"
                          value={stats?.clockOutTime ? new Date(stats.clockOutTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '--:--'}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Col>
                    </Row>
                  </div>

                  <Button
                    type="primary"
                    block
                    icon={<ClockCircleOutlined />}
                    onClick={() => navigate('/technician/clock-in')}
                  >
                    {stats?.clockOutTime ? 'View Time Entries' : 'Clock Out'}
                  </Button>
                </Space>
              </Card>
            </Space>
          </Col>
        </Row>

        {/* Weekly Performance */}
        <Card title="This Week's Performance">
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Statistic
                title="Work Orders Completed"
                value={stats?.workOrdersCompletedWeek || 0}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Average Completion Time"
                value={(stats?.averageCompletionTime || 0).toFixed(1)}
                suffix="hrs"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Total Hours"
                value={(stats?.hoursLoggedWeek || 0).toFixed(1)}
                suffix="hrs"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Efficiency Rating"
                value={Math.round(stats?.efficiencyRating || 0)}
                suffix="%"
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
          </Row>
        </Card>
      </Space>
    </div>
  );
};

export default TechnicianDashboard;
